/**
 * Pick a foreground color (light or dark text token) that has reasonable
 * contrast against the given hex background. Uses YIQ luminance — fast
 * and good enough for chip backgrounds.
 *
 * Accepts long-form (`#abcdef`) and short-form (`#abc`) hex, with or
 * without the leading `#`. Returns one of two CSS variable tokens:
 *
 *  - `var(--gray-900)` for dark text on a light background
 *  - `var(--gray-100)` for light text on a dark background
 *
 * Returns the light-text token for unparseable input (matches the
 * sidebar's dark theme — readable on whatever falls through).
 */
export const contrastingTextColor = (hex: string): string => {
  const normalized = normalizeHex(hex);
  if (!normalized) return 'var(--gray-100)';
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? 'var(--gray-900)' : 'var(--gray-100)';
};

/**
 * Normalize a hex string to `#RRGGBB` (uppercase or lowercase preserved).
 * Returns null for unparseable input.
 *
 * Accepts:
 *  - `#RRGGBB` or `RRGGBB`
 *  - `#RGB` or `RGB` (expanded by doubling each digit)
 */
const normalizeHex = (input: string): string | null => {
  if (!input) return null;
  const withHash = input.startsWith('#') ? input : `#${input}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash;
  if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
    const [, r, g, b] = withHash;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
};
